import { isVirtualRingId } from 'business/tunnel/pages/ring-scan/constants';
import type { SegmentUploadV1OutputDto } from 'generated/apiSchemas';
import { db, type Segment } from 'technical/db';

export type BackendSegment = {
  serialNumber: string;
  segmentType: string;
  status?: string | undefined;
  ringId?: string | null | undefined;
  virtualRingNumber?: number | undefined;
};

export const segmentSyncService = {
  async download(backendSegments: BackendSegment[]): Promise<void> {
    // A segment can have local changes not yet uploaded (`desynchronizedAt !== null`) when a
    // download runs — e.g. the app navigates away before the upload request completes. Applying
    // the backend's (older) version in that case would silently discard the pending local scan.
    const localSegments = await db.segments.bulkGet(
      backendSegments.map((s) => s.serialNumber),
    );
    const pendingSerialNumbers = new Set(
      localSegments
        .filter((s) => s?.desynchronizedAt != null)
        .map((s) => s?.serialNumber),
    );

    await db.segments.bulkPut(
      backendSegments
        .filter((remote) => !pendingSerialNumbers.has(remote.serialNumber))
        .map((remote) => ({
          serialNumber: remote.serialNumber,
          segmentType: remote.segmentType,
          status: remote.status,
          ringId: remote.ringId,
          virtualRingNumber: remote.virtualRingNumber,
          desynchronizedAt: null,
        })),
    );
  },

  async upload(
    uploadSegments: (segments: Segment[]) => Promise<SegmentUploadV1OutputDto>,
  ): Promise<void> {
    const segments = await db.segments
      .filter((s) => s.desynchronizedAt !== null)
      .toArray();

    const sanitized = segments.map((s) => ({
      ...s,
      ringId: s.ringId && isVirtualRingId(s.ringId) ? undefined : s.ringId,
    }));

    await uploadSegments(sanitized);

    await db.segments.bulkUpdate(
      segments.map((segment) => ({
        key: segment.serialNumber,
        changes: { desynchronizedAt: null },
      })) ?? [],
    );
  },
};
